import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';

const defaultCurrencyLocale = 'vi-VN';
const defaultCurrency = 'VND';

const currencyFormatter = (locale = defaultCurrencyLocale, currency = defaultCurrency, maximumFractionDigits = 0) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits,
  });

const compactFormatter = (locale = defaultCurrencyLocale, maximumFractionDigits = 1) =>
  new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits,
  });

function toDate(value: string | Date | undefined | null): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  try {
    return parseISO(value);
  } catch (error) {
    return null;
  }
}

export function formatCurrency(
  value: number | null | undefined,
  locale = defaultCurrencyLocale,
  currency = defaultCurrency,
  maximumFractionDigits = 0,
): string {
  if (value == null || Number.isNaN(value)) {
    return '₫0';
  }
  return currencyFormatter(locale, currency, maximumFractionDigits).format(value);
}

export function formatCompactCurrency(
  value: number | null | undefined,
  locale = defaultCurrencyLocale,
  maximumFractionDigits = 1,
): string {
  if (value == null || Number.isNaN(value)) {
    return '0';
  }
  return compactFormatter(locale, maximumFractionDigits).format(value);
}

interface FormatDecimalOptions {
  locale?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

export function formatDecimal(
  value: number | null | undefined,
  {
    locale = defaultCurrencyLocale,
    maximumFractionDigits = 6,
    minimumFractionDigits = 0,
  }: FormatDecimalOptions = {},
): string {
  if (value == null || Number.isNaN(value)) {
    return '0';
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

export function formatDateTime(value: string | Date | undefined, pattern = 'dd MMM yyyy HH:mm'): string {
  const date = toDate(value);
  if (!date) {
    return '—';
  }
  return format(date, pattern);
}

export function formatRelativeTime(value: string | Date | undefined): string {
  const date = toDate(value);
  if (!date) {
    return '—';
  }
  return formatDistanceToNowStrict(date, { addSuffix: true });
}
